import React, { useEffect, useState } from 'react';
import './BudgetBudgetLines.css';
import privateApi from '../../apis/privateApi';
// import { useSelector } from "react-redux";
import Select from "react-select";
import ConfirmDialog from '../../components/ConfirmDialog/ConfirmDialog';
import { Link, useLocation, useNavigate } from "react-router-dom";
import { FiEye, FiEdit, FiTrash2, FiChevronLeft, FiChevronRight } from "react-icons/fi";

const flattenSegments = (segments, level = 0) => {
    let result = [];

    segments.forEach(item => {
        result.push({
            value: item._id,
            label: "\u00A0".repeat(level * 3) + item.name,
            level,
            isLeaf: !item.hasChildren,
        });

        if (item.children?.length) {
            result = result.concat(flattenSegments(item.children, level + 1));
        }
    });

    return result;
};

const selectStyles = {
    control: (base, state) => ({
        ...base,
        borderColor: state.isFocused ? "#006B3C" : "#D2DDD5",
        boxShadow: state.isFocused
            ? "0 0 0 3px rgba(0,107,60,.18)"
            : "none",
        "&:hover": {
            borderColor: "#006B3C",
        },
        borderRadius: 6,
        minHeight: 38,
        fontSize: 13,
    }),

    menu: base => ({
        ...base,
        zIndex: 9999,
    }),
};

const SegSelect = ({
    label,
    options,
    value,
    onChange,
    placeholder,
}) => (
    <div className="db-seg-group">
        <span className="db-seg-label">{label}</span>

        <Select
            classNamePrefix="react-select"
            styles={selectStyles}
            options={options}
            value={options.find(o => o.value === value) || null}
            onChange={opt => onChange(opt?.value || "")}
            placeholder={placeholder}
            isSearchable
            isClearable
        />
    </div>
);

const BudgetBudgetLines = () => {
    const locations = useLocation();
    const navigate = useNavigate();
    const budget = locations?.state?.budget;
    // const role = useSelector(state => state.permissions.permissions);

    const [budgetLines, setBudgetLines] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const [filters, setFilters] = useState({
        budgetId: budget._id,
        minAmount: '',
        maxAmount: '',
    });

    const [pagination, setPagination] = useState({
        page: 1, limit: 10, totalPages: 1, totalRecords: 0,
    });

    const [confirmOpen, setConfirmOpen] = useState(false);
    const [confirmToken, setConfirmToken] = useState('');
    const [deleteId, setDeleteId] = useState(null);

    const [location, setLocation] = useState("");

    const [administrativeUnitId, setAdministrativeUnitId] = useState("");
    const [programmeId, setProgrammeId] = useState("");
    const [economicSegmentId, setEconomicSegmentId] = useState("");
    const [fundId, setFundId] = useState("");
    const [functionalSegmentId, setFunctionalSegmentId] = useState("");

    const [administrativeUnits, setAdministrativeUnits] = useState([]);
    const [programmes, setProgrammes] = useState([]);
    const [economicSegments, setEconomicSegments] = useState([]);
    const [funds, setFunds] = useState([]);
    const [functions, setFunctions] = useState([]);

    const administrativeUnitOptions = flattenSegments(administrativeUnits);
    const programmeOptions = flattenSegments(programmes);
    const economicSegmentOptions = flattenSegments(economicSegments);
    const fundOptions = flattenSegments(funds);
    const functionOptions = flattenSegments(functions);

    const fetchBudgetLines = async (overridePage) => {
        try {
            setLoading(true);
            setError('');
            const query = new URLSearchParams({
                ...filters,

                scope: location,

                administrativeUnitId,
                programmeId,
                economicSegmentId,
                fundId,
                functionalSegmentId,

                page: overridePage ?? pagination.page,
                limit: pagination.limit,
            });
            const { data } = await privateApi.get(`/api/budget/budget-lines?${query}`);
            if (data.success) {
                setBudgetLines(data.data);
                setPagination(prev => ({
                    ...prev,
                    totalPages: data.pagination?.pages || 1,
                    totalRecords: data.pagination?.total || data.data.length,
                }));
            } else {
                setBudgetLines([]);
                setError('No records found');
            }
        } catch (err) {
            setBudgetLines([]);
            setError(err.response?.data?.message || 'Failed to fetch budget lines.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const initialFetchBudgetLines = async (overridePage) => {
            try {
                setLoading(true);
                setError('');
                const query = new URLSearchParams({
                    ...filters,

                    scope: location,

                    administrativeUnitId,
                    programmeId,
                    economicSegmentId,
                    fundId,
                    functionalSegmentId,

                    page: overridePage ?? pagination.page,
                    limit: pagination.limit,
                });
                const { data } = await privateApi.get(`/api/budget/budget-lines?${query}`);
                if (data.success) {
                    setBudgetLines(data.data);
                    setPagination(prev => ({
                        ...prev,
                        totalPages: data.pagination?.pages || 1,
                        totalRecords: data.pagination?.total || data.data.length,
                    }));
                } else {
                    setBudgetLines([]);
                    setError('No records found');
                }
            } catch (err) {
                setBudgetLines([]);
                setError(err.response?.data?.message || 'Failed to fetch budget lines.');
            } finally {
                setLoading(false);
            }
        };

        initialFetchBudgetLines();
    }, [
        filters,
        pagination.page,
        pagination.limit,

        location,
        administrativeUnitId,
        programmeId,
        economicSegmentId,
        fundId,
        functionalSegmentId
    ]);

    useEffect(() => {
        if (!location) return;

        (async () => {
            try {
                const [
                    adminRes,
                    progRes,
                    econRes,
                    fundRes,
                    funcRes,
                ] = await Promise.all([
                    privateApi.get(`/api/get-create-budget-line-administrative-units?level=${location}`),
                    privateApi.get(`/api/get-create-budget-line-programmes?level=${location}`),
                    privateApi.get(`/api/get-create-budget-line-economic-segments?level=${location}`),
                    privateApi.get(`/api/get-create-budget-line-funds?level=${location}`),
                    privateApi.get(`/api/get-create-budget-line-functional-segments?level=${location}`),
                ]);

                setAdministrativeUnits(adminRes.data.data || []);
                setProgrammes(progRes.data.data || []);
                setEconomicSegments(econRes.data.data || []);
                setFunds(fundRes.data.data || []);
                setFunctions(funcRes.data.data || []);
            } catch (err) {
                console.error(err);
            }
        })();
    }, [location]);

    const handleChange = e => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value }));
    };

    const applyFilters = () => {
        setPagination(prev => ({ ...prev, page: 1 }));
        fetchBudgetLines(1);
    };

    const handleDeleteClick = id => {
        const token = Math.random().toString(36).substring(2, 8).toUpperCase();
        setDeleteId(id);
        setConfirmToken(token);
        setConfirmOpen(true);
    };

    const handleConfirmDelete = async input => {
        if (input !== confirmToken) {
            alert('Incorrect confirmation code.');
            setConfirmOpen(false);
            return;
        }
        try {
            await privateApi.delete(`/api/budget-lines/${deleteId}`);
            fetchBudgetLines();
        } catch (err) {
            alert(err.response?.data?.message || 'Delete failed.');
        } finally {
            setConfirmOpen(false);
            setDeleteId(null);
        }
    };

    const locationName = budget?.stateId?.name || budget?.lgaId?.name || 'Budget';

    return (
        <div className="bgt-page">

            {/* HEADER */}
            <div className="bbl-header">
                <div className="bbl-header-left">
                    <h1 className="bbl-header-left">Budget Lines</h1>
                    <p>{locationName} · {budget?.year} · {budget?.type}</p>
                </div>
                <button className="bgt-btn bgt-btn-secondary" onClick={() => navigate(-1)}>
                    <FiChevronLeft size={14} /> Back
                </button>
            </div>

            {/* TOOLBAR */}
            <div className="bbl-toolbar">
                <span className="bbl-filter-label">Filter</span>
                <input className="bbl-filter-input" name="minAmount" placeholder="Min Amount"
                    value={filters.minAmount} onChange={handleChange} />
                <input className="bbl-filter-input" name="maxAmount" placeholder="Max Amount"
                    value={filters.maxAmount} onChange={handleChange} />
                <button className="bgt-btn bgt-btn-primary" onClick={applyFilters}>Search</button>

                <div className="bbl-filter-divider" />
                <div className="bbl-filter-spacer" />

                <div className="bbl-per-page-wrap">
                    <span className="bbl-per-page-label">Rows</span>
                    <select className="bbl-per-page-select" value={pagination.limit}
                        onChange={e => setPagination(prev => ({ ...prev, limit: parseInt(e.target.value, 10), page: 1 }))}>
                        <option value={10}>10</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                    </select>
                </div>
            </div>

            <div className="db-segment-bar">

                <div className="db-seg-group">
                    <span className="db-seg-label">Budget Level</span>

                    <Select
                        classNamePrefix="react-select"
                        styles={selectStyles}
                        options={[
                            { value: "state", label: "State" },
                            { value: "lga", label: "LGA" }
                        ]}
                        value={
                            location
                                ? {
                                    value: location,
                                    label:
                                        location.charAt(0).toUpperCase() +
                                        location.slice(1)
                                }
                                : null
                        }
                        onChange={opt => setLocation(opt?.value || "")}
                        placeholder="Select level..."
                        isClearable
                    />
                </div>

                <SegSelect
                    label="Admin Unit"
                    options={administrativeUnitOptions}
                    value={administrativeUnitId}
                    onChange={setAdministrativeUnitId}
                    placeholder="Select unit..."
                />

                <SegSelect
                    label="Economic Seg."
                    options={economicSegmentOptions}
                    value={economicSegmentId}
                    onChange={setEconomicSegmentId}
                    placeholder="Select segment..."
                />

                <SegSelect
                    label="Functional Seg."
                    options={functionOptions}
                    value={functionalSegmentId}
                    onChange={setFunctionalSegmentId}
                    placeholder="Select function..."
                />

                <SegSelect
                    label="Fund Seg."
                    options={fundOptions}
                    value={fundId}
                    onChange={setFundId}
                    placeholder="Select fund..."
                />

                <SegSelect
                    label="Programme Seg."
                    options={programmeOptions}
                    value={programmeId}
                    onChange={setProgrammeId}
                    placeholder="Select programme..."
                />
            </div>

            {/* TABLE CARD */}
            <div className="bbl-table-card">
                {loading ? (
                    <div className="bgt-loading-wrap">
                        <div className="bgt-spinner" />
                        <p className="bgt-loading-text">Loading Budget Lines…</p>
                    </div>
                ) : error ? (
                    <div className="bbl-error">⚠ {error}</div>
                ) : budgetLines.length === 0 ? (
                    <div className="bbl-empty">
                        <span className="bbl-empty-icon">📋</span>
                        No budget lines found.
                    </div>
                ) : (
                    <div className="bbl-table-scroll">
                        <table className="bbl-table">
                            <thead>
                                <tr>
                                    <th>Admin Unit</th>
                                    <th>Economic Seg.</th>
                                    <th>Functional Seg.</th>
                                    <th>Fund</th>
                                    <th>Programme</th>
                                    <th>State</th>
                                    <th>LGA</th>
                                    <th>Amount (₦)</th>
                                    <th>Spent (₦)</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {budgetLines.map(line => (
                                    <tr key={line._id}>
                                        <td>{line.administrativeUnitId?.description || '—'}</td>
                                        <td>{line.economicSegmentId?.description || '—'}</td>
                                        <td>{line.functionalSegmentId?.description || '—'}</td>
                                        <td>{line.fundId?.description || '—'}</td>
                                        <td>{line.programmeId?.description || '—'}</td>
                                        <td>{line.stateId?.name || '—'}</td>
                                        <td>{line.lgaId?.name || '—'}</td>
                                        <td className="amount">{line.amount?.toLocaleString() || '0'}</td>
                                        <td className="spent">{line.spentAmount?.toLocaleString() || '0'}</td>
                                        <td>
                                            <div className="bbl-actions">
                                                <Link className="bbl-icon-btn" title="View"
                                                    to="/budget-lines/detail" state={{ budgetLine: line }}>
                                                    <FiEye size={14} />
                                                </Link>
                                                {line.budgetStatus === 'draft' && (
                                                    <Link className="bbl-icon-btn edit" title="Edit"
                                                        to="/budget-lines/edit" state={{ budgetLine: line }}>
                                                        <FiEdit size={14} />
                                                    </Link>
                                                )}
                                                {line.budgetStatus === 'draft' && (
                                                    <button className="bbl-icon-btn delete" title="Delete"
                                                        onClick={() => handleDeleteClick(line._id)}>
                                                        <FiTrash2 size={14} />
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* PAGINATION */}
            {budgetLines.length > 0 && (
                <div className="bbl-pagination">
                    <button className="bbl-page-btn"
                        onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}
                        disabled={pagination.page === 1}>
                        <FiChevronLeft size={13} /> Prev
                    </button>
                    <span className="bbl-page-info">
                        Page {pagination.page} of {pagination.totalPages}
                        <span style={{ marginLeft: 8, opacity: .6 }}>({pagination.totalRecords} total)</span>
                    </span>
                    <button className="bbl-page-btn"
                        onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}
                        disabled={pagination.page === pagination.totalPages}>
                        Next <FiChevronRight size={13} />
                    </button>
                </div>
            )}

            <ConfirmDialog show={confirmOpen} token={confirmToken}
                action="You are about to delete this budget line"
                onConfirm={handleConfirmDelete}
                onCancel={() => { setConfirmOpen(false); setDeleteId(null); }} />
        </div>
    );
};

export default BudgetBudgetLines;